import { createFileRoute, useRouteContext, useRouter } from "@tanstack/react-router";
import Left from "@repo/icons/default/Left";
import { Button } from "@/components/ui/button/button";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page, PageSection, PageTitle } from "@/components/ui/layout/page/page";
import { trackEvent } from "@/lib/tracking";
import { ICON_MAP } from "@/lib/constants/fake-door-home-icons";

import type { JSX } from "react";

const processMarkdownText = (text: string): React.ReactNode[] => {
  const parts = text.split(/(?<text>\*\*.*?\*\*)/g);
  return parts.map((part) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      const boldText = part.slice(2, -2);
      return <b key={boldText}>{boldText}</b>;
    }
    return part;
  });
};

const InfoItemDetailsPage = (): JSX.Element => {
  const { flags } = useRouteContext({ from: "__root__" });
  const INTEGRATIONS_FF_JSON = flags["textos-rota-info-home"];
  const { infoItemId } = Route.useParams();
  const router = useRouter();

  const integration = INTEGRATIONS_FF_JSON?.[infoItemId];

  if (!integration) {
    return (
      <>
        <HeaderRoot>
          <HeaderButton icon={Left} align="start" />
          <HeaderTitle title="Integração não encontrada" align="center" />
        </HeaderRoot>
        <Page>
          <PageSection className="px-4 flex flex-col gap-6 h-full">
            <div className="flex flex-col items-center justify-center gap-6 text-center">
              <p>Integração não encontrada.</p>
            </div>
          </PageSection>
        </Page>
      </>
    );
  }

  const handleButtonClick = (): void => {
    trackEvent(integration.eventName, integration.eventProps);
    void router.navigate({
      to: "/home/info/$infoItemId/confirmation",
      params: { infoItemId },
    });
  };

  const IconComponent =
    integration.iconName && integration.iconName in ICON_MAP
      ? ICON_MAP[integration.iconName as keyof typeof ICON_MAP]
      : undefined;

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title={integration.title} align="center" />
      </HeaderRoot>
      <Page className="p-4">
        <PageSection className="p-4 flex flex-col gap-6">
          <div className="flex flex-col items-center justify-center gap-2 text-center">
            {IconComponent ? (
              <IconComponent size={76} className="fill-secondary-500" />
            ) : null}
            <PageTitle className="text-center text-lg">{integration.pageTitle}</PageTitle>
            {integration.description ? <p>{integration.description}</p> : null}
          </div>

          <ul className="flex flex-col gap-2 px-6 py-4 text-sm font-normal text-neutral-600 bg-neutral-50 rounded-lg shrink-0">
            {integration.features.map((feature) => (
              <li key={feature.title} className="flex items-start gap-3">
                <div className="w-2.5 h-2.5 bg-primary-500 rounded-full shrink-0 mt-1.5" />
                <span>
                  {processMarkdownText(feature.title)}: {feature.description}
                </span>
              </li>
            ))}
          </ul>

          <p className="text-center">{processMarkdownText(integration.pricingText)}</p>

          <Button fullWidth onClick={handleButtonClick}>
            <div className="flex flex-row items-center justify-center gap-2">
              {integration.buttonText}
            </div>
          </Button>
        </PageSection>
      </Page>
    </>
  );
};

export const Route = createFileRoute("/home/info/$infoItemId/")({
  component: InfoItemDetailsPage,
});
